import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportCheckboxField,
  OtherReportFilterCard,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportShell,
  OtherReportToolbar,
  OtherReportEmptyCells,
} from './other-reports/OtherReportShared';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeDateKey = (value) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return String(value);
};

const buildRowsWithDateTotals = (rows, dateWiseTotals) => {
  if (!dateWiseTotals || rows.length === 0) {
    return rows.map((row, index) => ({ type: 'data', key: `row-${index}`, row }));
  }

  const displayRows = [];
  let currentDateKey = null;
  let dateAmountSum = 0;
  let dateReceivedSum = 0;
  let dateQuantitySum = 0;
  let datePriceSum = 0;

  const pushDateTotal = (dateKey, index) => {
    displayRows.push({
      type: 'date_total',
      key: `date-total-${dateKey}-${index}`,
      totals: {
        amount: dateAmountSum,
        recieved_amount: dateReceivedSum,
        quantity: dateQuantitySum,
        price: datePriceSum,
      },
    });
  };

  rows.forEach((row, index) => {
    const dateKey = normalizeDateKey(row.ac_date);
    if (currentDateKey !== null && dateKey !== currentDateKey) {
      pushDateTotal(currentDateKey, index);
      dateAmountSum = 0;
      dateReceivedSum = 0;
      dateQuantitySum = 0;
      datePriceSum = 0;
    }

    currentDateKey = dateKey;
    dateAmountSum += toNumber(row.amount);
    dateReceivedSum += toNumber(row.recieved_amount);
    dateQuantitySum += toNumber(row.quantity);
    datePriceSum += toNumber(row.price);
    displayRows.push({ type: 'data', key: `row-${index}`, row });
  });

  if (currentDateKey !== null) {
    pushDateTotal(currentDateKey, rows.length);
  }

  return displayRows;
};

const MlaCouponStatementReport = (props) => {
  const [meta, setMeta] = useState({});
  const [inputValues, setInputValues] = useState({ summary: false });
  const [dateWiseTotals, setDateWiseTotals] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/mla_coupon_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('MLA Coupon Statement', error.message, 'error'));
  }, []);

  const loadReport = async () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return;
    }

    setLoading(true);
    setReport(null);
    const payload = { from_date: inputValues.start_date, to_date: inputValues.end_date };
    if (inputValues.summary) payload.summary = 1;

    try {
      setReport(await fetchReportJson(`${API_URL}/mla_coupon_statement`, payload));
    } catch (error) {
      swal('MLA Coupon Statement', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const payload = { from_date: inputValues.start_date, to_date: inputValues.end_date };
    if (inputValues.summary) {
      payload.summary = 1;
    }
    return payload;
  };

  const downloadPdf = async () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/mla_coupon_statement/pdf`,
        buildPayload(),
        'mla_coupon_statement.pdf',
      );
    } catch (error) {
      swal('MLA Coupon Statement', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const openingRows = (report?.opening_balance || []).filter((row) => Number(row.outstanding || 0) !== 0);

  const totalsRow = useMemo(() => {
    if (report?.totals_row) {
      return report.totals_row;
    }
    const rows = report?.rows || [];
    return {
      label: 'Total',
      quantity: rows.reduce((sum, row) => sum + toNumber(row.quantity), 0),
      price: rows.reduce((sum, row) => sum + toNumber(row.price), 0),
      amount: rows.reduce((sum, row) => sum + toNumber(row.amount), 0),
      recieved_amount: rows.reduce((sum, row) => sum + toNumber(row.recieved_amount), 0),
    };
  }, [report]);

  const displayRows = useMemo(
    () => buildRowsWithDateTotals(report?.rows || [], dateWiseTotals),
    [report?.rows, dateWiseTotals],
  );

  const renderDateTotalRow = (totals) => {
    if (report.is_summary) {
      return (
        <tr key={totals.key} className="table-secondary fw-semibold">
          <td>Total</td>
          <td className="report-amount">{reportNumberFormat(totals.totals.amount)}</td>
          <td className="report-amount">{reportNumberFormat(totals.totals.recieved_amount)}</td>
          <td />
        </tr>
      );
    }

    return (
      <tr key={totals.key} className="table-secondary fw-semibold">
        <td colSpan={8}>Total</td>
        <td className="report-amount">{reportNumberFormat(totals.totals.amount)}</td>
        <td className="report-amount">{reportNumberFormat(totals.totals.recieved_amount)}</td>
        <td />
      </tr>
    );
  };

  const renderDataRow = (row, key) => {
    if (report.is_summary) {
      return (
        <tr key={key}>
          <td>{formatReportDate(row.ac_date)}</td>
          <td className="report-amount">{reportNumberFormat(row.amount)}</td>
          <td className="report-amount">{reportNumberFormat(row.recieved_amount)}</td>
          <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
        </tr>
      );
    }

    return (
      <tr key={key}>
        <td>{formatReportDate(row.ac_date)}</td>
        <td>{formatReportDate(row.issue_date)}</td>
        <td>{row.expiry_date || ''}</td>
        <td>{row.coupon_no || ''}</td>
        <td>{row.description || ''}</td>
        <td>{row.product || ''}</td>
        <td className="report-amount">{row.quantity ?? ''}</td>
        <td className="report-amount">{reportNumberFormat(row.price)}</td>
        <td className="report-amount">{reportNumberFormat(row.amount)}</td>
        <td className="report-amount">{reportNumberFormat(row.recieved_amount)}</td>
        <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
      </tr>
    );
  };

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="MLA Coupon Statement" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                    <OtherReportCheckboxField
                      id="mla-summary-toggle"
                      label="Summary mode"
                      checked={inputValues.summary}
                      onChange={(checked) => setInputValues({ ...inputValues, summary: checked })}
                    />
                  </>
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating MLA coupon statement" />}
              {report && !loading && (
              <div className="form-wrap balace-sheetwrap">
                <div className="row mb-3">
                  <div className="col-md">
                    <h2 className="table-title mb-1">{report.pump_name}</h2>
                    <h4 className="table-title mb-1">{report.type}</h4>
                    <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                    <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                  </div>
                  <div className="col-md-auto d-flex align-items-end justify-content-md-end mt-2 mt-md-0">
                    <button
                      type="button"
                      className={`btn btn-sm ${dateWiseTotals ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setDateWiseTotals((value) => !value)}
                      disabled={!(report.rows || []).length}
                    >
                      {dateWiseTotals ? 'Show flat list' : 'Group by date'}
                    </button>
                  </div>
                </div>
                <table className="table border">
                  <thead>
                    {report.is_summary ? (
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Received amount</th>
                        <th>Outstanding amount</th>
                      </tr>
                    ) : (
                      <tr>
                        <th>Date</th><th>Issue date</th><th>Expiry</th><th>Coupon no</th>
                        <th>Description</th><th>Product</th><th>Qty</th><th>Price</th>
                        <th>Amount</th><th>Received</th><th>Outstanding</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {openingRows.map((row, i) => (
                      <tr key={`open-${i}`} className="table-light">
                        <td>{formatReportDate(row.ac_date) || formatReportDate(report.start_date)}</td>
                        {report.is_summary ? (
                          <>
                            <td />
                            <td />
                            <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                          </>
                        ) : (
                          <>
                            <OtherReportEmptyCells count={9} />
                            <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                    {displayRows.map((entry) => (
                      entry.type === 'date_total'
                        ? renderDateTotalRow(entry)
                        : renderDataRow(entry.row, entry.key)
                    ))}
                    {(report.rows || []).length > 0 && (
                      <tr className="fw-bold">
                        {report.is_summary ? (
                          <>
                            <td>{dateWiseTotals ? 'Grand total' : (totalsRow.label || 'Total')}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.amount)}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.recieved_amount)}</td>
                            <td />
                          </>
                        ) : (
                          <>
                            <td colSpan={6}>{dateWiseTotals ? 'Grand total' : (totalsRow.label || 'Total')}</td>
                            <td className="report-amount">{totalsRow.quantity}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.price)}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.amount)}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.recieved_amount)}</td>
                            <td />
                          </>
                        )}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default MlaCouponStatementReport;
